import React, { useEffect, useState } from 'react';
import { Text } from '@react-three/drei';
import { useCityStore } from '../city/cityStore';
import { WELCOME_SIGN } from '../city/cityZones';
import { baseGroundY } from '../city/cityGround';
import { serverNow } from '../services/worldSocket';

// WelcomeSign — the first thing a visitor reads, standing where they arrive.
//
// The city is open while it is being built, which means people walk into empty
// rooms, unfinished streets and things that move between visits. Said plainly
// up front, that reads as an invitation. Left unsaid, the same city reads as
// broken — the visitor assumes they are seeing a finished thing done badly.
//
// So this is deliberately honest rather than promotional: it names what is
// missing before anyone discovers it themselves. It also sets the return hook —
// "it won't be the same place" is the reason to come back tomorrow.
//
// Placed in world space rather than as HUD copy so it belongs to the street and
// can be walked past, not dismissed. Tune live: ?signx ?signy ?signz ?signrot
// ?signscale — then paste the values into the defaults below.
const qp = (k, d) => {
  const v = typeof window !== 'undefined' && parseFloat(new URLSearchParams(window.location.search).get(k));
  return Number.isFinite(v) ? v : d;
};

const ACCENT = '#ffb25e';
const TEXT = '#f2e6d4';

// Separately POSITIONED blocks rather than one string with blank lines.
// Blank lines make the block taller, and because drei's Text centres on its
// anchor, a taller block grows UPWARD as well as down — which is how the body
// climbed over the subheader. Explicit y per block means adding a line can
// never move the one above it.
//
// Read at walking pace by someone who did not come here to read. Six lines,
// each carrying one idea: it's unfinished · who is out right now · it's live ·
// there are shows and places to go in · the artists talk and remember you. Anything longer gets skipped, and a skipped sign is the same as
// no sign — which is the failure this exists to prevent.
//
// Board is 2.3 tall (±1.15). Block centres, top to bottom:
//   title 0.82 · subhead 0.58 · the city now 0.25 · message of the day −0.33 ·
//   sign-off −0.92
// THE MESSAGE OF THE DAY (Khari, 2026-10-23: *"We can use this sign to give
// messages of the day, etc."*). platform_settings `world_sign_message`
// (API migration 415), shipped with the city snapshot; this is the default
// until it is set, and whenever the city is not reachable.
// Khari, 2026-10-29: *"a bit more about the city being live in realtime.
// artists that chat and remember you, events at the plaza + venues to explore"*
// (API migration 428 puts the same words in the setting).
const DEFAULT_MESSAGE = 'The city runs live, in real time.\nShows on the plaza stage. Venues to explore.';

// Where there is no live city to report on (an API without it), the line that
// argued the place is real before the artists lived here.
const FALLBACK_STATUS = 'The clock runs on real Oakland time,\nand the radio is genuinely playing.';

// Closes on the promise rather than the disclaimer. Everything above admits
// what is missing; this is the line that says why it is worth your time anyway —
// and it is the one a visitor will still be carrying when they leave.
const SIGNOFF = 'The artists chat back, and they remember you.';

const WORDS = ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve'];

/**
 * THE CITY RIGHT NOW, in one line — how many artists are out, never WHERE
 * (Khari, 2026-10-22: *"we don't tell people where things are happening"*).
 * When nobody is: *"The artists have gone home for the night."* (his words,
 * 2026-10-23), and when the first one is back.
 */
function useCityStatus() {
  const snap = useCityStore((st) => st.snap);
  const live = useCityStore((st) => st.live);
  const [tick, setTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setTick((x) => x + 1), 20000); return () => clearInterval(t); }, []);
  void tick;
  if (!live || !snap?.residents?.length) return null;
  const now = serverNow();
  const tz = snap.tz || 'America/Los_Angeles';
  const hour = Number(new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', hour12: false }).format(new Date(now))) % 24;
  const night = hour >= 20 || hour < 6;
  let out = 0; let nextBack = null;
  for (const r of snap.residents) {
    for (const s of r.segs || []) {
      if (s.k === 'away') continue;
      if (s.t0 <= now && now < s.t1) { out += 1; break; }
      if (s.t0 > now && (nextBack == null || s.t0 < nextBack)) nextBack = s.t0;
    }
  }
  if (out === 0) {
    const first = night ? 'The artists have gone home for the night.' : 'The artists are all somewhere else right now.';
    if (nextBack == null) return first;
    const at = new Date(nextBack).toLocaleTimeString('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' }).toLowerCase();
    return `${first}\nThe first one is back around ${at}.`;
  }
  const n = WORDS[out] || String(out);
  if (night) return out === 1 ? 'One night owl is out in the city.\nFind them.' : `${n} night owls are out in the city.\nFind them.`;
  return out === 1 ? 'One of the artists is out in the city.\nFind them.' : `${n} of the artists are out in the city.\nFind them.`;
}

export default function WelcomeSign() {
  // WHERE IT STANDS: city/cityZones WELCOME_SIGN, shared with the street lamps
  // so none is ever placed through it. You arrive at PlanetScene's
  // MODEL_WALK_START, pos [0.18, EYE, 25.53], facing −z across a wide pavement
  // to a cross street (the road starts at z ≈ 18).
  //
  // History: 2026-10-11 it moved from dead ahead (a wall of text over the whole
  // first frame, on a phone the entire screen) to the right-hand kerb. On
  // 2026-10-23 a lamp the street generates along every kerb stood 1.1 units
  // from it, its post through the text, and the board's right half sat under
  // the map. It now stands back on the pavement, on its own posts, a little
  // lower and smaller, on the left (clear of the map), turned to face where
  // you land.
  const pos = [qp('signx', WELCOME_SIGN.x), qp('signy', WELCOME_SIGN.y), qp('signz', WELCOME_SIGN.z)];
  const rotY = qp('signrot', WELCOME_SIGN.rotY);
  const scale = qp('signscale', WELCOME_SIGN.scale);
  // The posts run to the pavement under it (city/cityGround), in board units.
  const footY = (baseGroundY(pos[0], pos[2]) - pos[1]) / scale;

  const status = useCityStatus() || FALLBACK_STATUS;
  const message = useCityStore((st) => st.snap?.sign?.message) || DEFAULT_MESSAGE;

  return (
    <group position={pos} rotation={[0, rotY, 0]} scale={scale}>
      {/* Board. Dark and matte so the text carries, and unlit enough to stay
          readable at any hour — a welcome that only works at noon is worse
          than none. */}
      <mesh position={[0, 0, -0.03]}>
        <planeGeometry args={[3.5, 2.3]} />
        <meshStandardMaterial color="#140e08" roughness={0.9} metalness={0} />
      </mesh>
      <mesh position={[0, 0, -0.02]}>
        <planeGeometry args={[3.38, 2.18]} />
        <meshStandardMaterial color="#1e150c" roughness={0.85} metalness={0} />
      </mesh>
      {/* Its back, so it is a board and not a sheet from behind. */}
      <mesh position={[0, 0, -0.06]} rotation={[0, Math.PI, 0]}>
        <planeGeometry args={[3.5, 2.3]} />
        <meshStandardMaterial color="#0d0a07" roughness={0.95} metalness={0} />
      </mesh>
      {/* Two posts down to the pavement. */}
      {[-1.3, 1.3].map((x) => (
        <mesh key={x} position={[x, (footY + 0.9) / 2, -0.1]} castShadow>
          <cylinderGeometry args={[0.045, 0.055, 0.9 - footY, 10]} />
          <meshStandardMaterial color="#1b1c20" roughness={0.6} metalness={0.6} />
        </mesh>
      ))}

      <Text
        position={[0, 0.82, 0]} fontSize={0.185} color={ACCENT}
        anchorX="center" anchorY="middle" letterSpacing={0.07} outlineWidth={0}
      >
        WELCOME TO HYPETOWN
      </Text>
      <Text
        position={[0, 0.58, 0]} fontSize={0.082} color="#b8956b"
        anchorX="center" anchorY="middle" letterSpacing={0.05}
      >
        — OPEN WHILE WE BUILD IT —
      </Text>
      {/* The city right now. ACCENT: the one claim a visitor can check on the spot. */}
      <Text
        position={[0, 0.25, 0]} fontSize={0.12} color={ACCENT}
        anchorX="center" anchorY="middle" lineHeight={1.4} textAlign="center" maxWidth={3.1}
      >
        {status}
      </Text>
      {/* The message of the day. */}
      <Text
        position={[0, -0.33, 0]} fontSize={0.108} color={TEXT}
        anchorX="center" anchorY="middle" lineHeight={1.45} textAlign="center" maxWidth={3.1}
      >
        {message}
      </Text>
      <Text
        position={[0, -0.92, 0]} fontSize={0.115} color={ACCENT}
        anchorX="center" anchorY="middle" lineHeight={1.45} textAlign="center" maxWidth={3.1}
      >
        {SIGNOFF}
      </Text>

      {/* A little warmth on the board so it reads as lit signage rather than a
          floating panel. Short range — it must not spill onto the street. */}
      <pointLight position={[0, 0.2, 0.9]} color={ACCENT} intensity={2.2} distance={3.2} />
    </group>
  );
}
